<template>
  <!-- 공모전 지원자 관리 (mimio#80). 지원 당시 포트폴리오 데이터로 그린다. 번호는 엑셀·ZIP 의 No 와 같다 -->
  <div class="contents">
    <div class="layout">
      <div class="ap-head">
        <div>
          <router-link :to="{ name: 'ContestManagement' }" class="ap-back">← 공모전 목록</router-link>
          <h2 class="ap-title">{{ contestTitle || '지원자 관리' }}</h2>
          <p class="ap-sub">지원 {{ rows.length }}건 · 이전 지원 {{ olderCount }}건 포함 (엑셀과 같은 목록·번호)</p>
        </div>
      </div>

      <div class="white-panel mt20">
        <p v-if="loading" class="ap-muted">불러오는 중…</p>
        <p v-else-if="loadError" class="ap-error">{{ loadError }}</p>
        <table v-else class="ap-table">
          <thead>
            <tr>
              <th style="width: 64px">No</th>
              <th>이름</th>
              <th style="width: 170px">지원일시</th>
              <th style="width: 90px">작품</th>
              <th style="width: 110px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.applicantId" class="ap-row" @click="open(r.applicantId)">
              <td>{{ r.no }}</td>
              <td>
                <b>{{ r.nameKo || '(이름 없음)' }}</b>
                <span v-if="r.nameEn" class="ap-muted"> {{ r.nameEn }}</span>
                <span v-if="!r.isLatest" class="ap-badge">이전 지원</span>
                <span v-if="!r.hasSnapshot" class="ap-badge ap-badge--warn">포트폴리오 정보 없음</span>
              </td>
              <td>{{ formatDateTime(r.appliedAt) }}</td>
              <td>{{ r.artworkCount }}점</td>
              <td><button type="button" class="ap-btn ap-btn--sm" @click.stop="open(r.applicantId)">보기</button></td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="5" class="ap-muted ap-center">지원자가 없습니다.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 좌우 분할 팝업: 왼쪽 목록 · 오른쪽 선택한 지원자 -->
    <div v-if="selectedId" class="ap-modal" @click.self="close">
      <div class="ap-modal__box">
        <!-- aside 태그는 전역 사이드메뉴 스타일(aside{position:fixed…})을 받는다 — div 로 둔다 -->
        <div class="ap-side">
          <div class="ap-side__head">지원자 {{ rows.length }}명</div>
          <ul class="ap-side__list">
            <li
              v-for="r in rows"
              :key="r.applicantId"
              :ref="(el) => setItemRef(r.applicantId, el)"
              :class="{ 'is-on': r.applicantId === selectedId }"
              @click="open(r.applicantId)"
            >
              <span class="ap-side__no">{{ r.no }}</span>
              <span class="ap-side__name">{{ r.nameKo || '(이름 없음)' }}</span>
              <span class="ap-side__count">{{ r.artworkCount }}점</span>
            </li>
          </ul>
        </div>

        <section class="ap-main">
          <div class="ap-main__bar">
            <button type="button" class="ap-btn ap-btn--sm" :disabled="!prevId" @click="open(prevId)">‹ 이전</button>
            <button type="button" class="ap-btn ap-btn--sm" :disabled="!nextId" @click="open(nextId)">다음 ›</button>
            <span class="ap-muted">←/→ 키로 넘길 수 있어요</span>
            <button type="button" class="ap-close" aria-label="닫기" @click="close">✕</button>
          </div>

          <div ref="mainScroll" class="ap-main__scroll">
            <p v-if="detailLoading" class="ap-muted">불러오는 중…</p>
            <p v-else-if="detailError" class="ap-error">{{ detailError }}</p>
            <template v-else-if="detail">
              <!-- 프로필 -->
              <div class="ap-profile">
                <div class="ap-profile__img">
                  <img v-if="d.profile?.imageUrl && !broken[d.profile.imageUrl]" :src="d.profile.imageUrl" alt="" @error="markBroken(d.profile.imageUrl)" />
                  <div v-else class="ap-deleted ap-deleted--round"><span>{{ d.profile?.imageUrl ? '삭제된 이미지' : '사진 없음' }}</span></div>
                </div>
                <div class="ap-profile__info">
                  <div class="ap-profile__no">No {{ d.no }}<span v-if="!d.isLatest" class="ap-badge">이전 지원</span></div>
                  <h3>{{ d.profile?.nameKo || '(이름 없음)' }} <small>{{ d.profile?.nameEn }}</small></h3>
                  <p v-if="d.profile?.genres" class="ap-muted">{{ d.profile.genres }}</p>
                  <p class="ap-meta">
                    <span v-if="d.profile?.email">{{ d.profile.email }}</span>
                    <span v-if="d.profile?.phoneNum">{{ d.profile.phoneNum }}</span>
                    <span>지원 {{ formatDateTime(d.appliedAt) }}</span>
                  </p>
                </div>
              </div>

              <p v-if="!d.hasSnapshot" class="ap-note">
                이 지원서에는 포트폴리오 데이터가 없습니다. 아래 지원서 답변과 PDF 로 확인해주세요.
              </p>

              <!-- 지원서 답변 (엑셀 열과 같음) -->
              <details class="ap-block" open>
                <summary>지원서 답변</summary>
                <dl class="ap-answers">
                  <template v-for="(a, i) in d.answers" :key="i">
                    <dt>{{ a.label }}</dt>
                    <dd>{{ a.value || '-' }}</dd>
                  </template>
                </dl>
              </details>

              <div v-if="d.profile?.introduction" class="ap-block">
                <h4>소개</h4>
                <p class="ap-text">{{ d.profile.introduction }}</p>
              </div>
              <div v-if="d.profile?.artistNote" class="ap-block">
                <h4>작가노트</h4>
                <p class="ap-text">{{ d.profile.artistNote }}</p>
              </div>

              <div v-if="d.profile?.sections?.length" class="ap-block ap-sections">
                <div v-for="s in d.profile.sections" :key="s.title" class="ap-section">
                  <h4>{{ s.title }}</h4>
                  <ul>
                    <li v-for="(it, i) in s.items" :key="i">{{ it }}</li>
                  </ul>
                </div>
              </div>

              <!-- 작품 — PDF 에 실린 순서 -->
              <div v-if="d.artworks?.length" class="ap-block">
                <h4>작품 {{ d.artworks.length }}점</h4>
                <div class="ap-works">
                  <figure v-for="(w, i) in d.artworks" :key="w.uuid || i" class="ap-work">
                    <div class="ap-work__img">
                      <img v-if="w.imageUrl && !broken[w.imageUrl]" :src="w.imageUrl" loading="lazy" alt="" @error="markBroken(w.imageUrl)" />
                      <div v-else class="ap-deleted">
                        <span>삭제된 이미지</span>
                        <small>지원 후 작가가 바꾸거나 지움 · PDF 에는 남아 있음</small>
                      </div>
                    </div>
                    <figcaption>
                      <b>{{ w.title || w.titleEn || '(제목 없음)' }}</b>
                      <span v-if="w.title && w.titleEn" class="ap-muted"> {{ w.titleEn }}</span>
                      <div class="ap-work__meta">
                        {{ [w.year, w.size, w.material, w.genre].filter(Boolean).join(' · ') }}
                      </div>
                      <p v-if="w.description" class="ap-work__desc">{{ w.description }}</p>
                    </figcaption>
                  </figure>
                </div>
              </div>
            </template>
          </div>

          <div class="ap-main__foot">
            <span class="ap-muted">화면은 지원 당시 데이터로 그린 것입니다. 최종 확인은 원본 PDF 로 해주세요.</span>
            <button type="button" class="ap-btn ap-btn--primary" :disabled="!d?.hasPdf || downloading" @click="downloadPdf">
              {{ downloading ? '준비 중…' : 'PDF 다운로드' }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { apiClient } from '@/services/auth-header';

const route = useRoute();
const uuid = route.params.uuid;
const base = `/contents/contests/${uuid}/applicants`;

const rows = ref([]);
const contestTitle = ref('');
const loading = ref(true);
const loadError = ref('');
const selectedId = ref(null);
const detail = ref(null);
const detailLoading = ref(false);
const detailError = ref('');
const downloading = ref(false);
const broken = reactive({});
const mainScroll = ref(null);
const itemRefs = {};

const d = computed(() => detail.value);
const olderCount = computed(() => rows.value.filter((r) => !r.isLatest).length);
const position = computed(() => rows.value.findIndex((r) => r.applicantId === selectedId.value));
const prevId = computed(() => (position.value > 0 ? rows.value[position.value - 1].applicantId : null));
const nextId = computed(() =>
  position.value >= 0 && position.value < rows.value.length - 1 ? rows.value[position.value + 1].applicantId : null,
);

function setItemRef(id, el) {
  if (el) itemRefs[id] = el;
}

function formatDateTime(v) {
  return v ? String(v).replace('T', ' ').slice(0, 16) : '-';
}

function errorMessage(e, fallback) {
  const data = e?.response?.data;
  if (typeof data === 'string' && data) return data;
  return data?.message ?? fallback;
}

// 이미지가 안 뜨면(작품 삭제·이미지 교체) 회색 「삭제된 이미지」 로 바꾼다
function markBroken(url) {
  broken[url] = true;
}

let seq = 0;
async function open(id) {
  if (!id) return;
  selectedId.value = id;
  const mine = ++seq;
  detailLoading.value = true;
  detailError.value = '';
  try {
    const { data } = await apiClient.get(`${base}/${id}`);
    if (mine !== seq) return; // 빠르게 넘길 때 늦게 온 옛 응답이 화면을 덮지 않게
    detail.value = data;
    await nextTick();
    mainScroll.value?.scrollTo({ top: 0 });
    itemRefs[id]?.scrollIntoView({ block: 'nearest' });
  } catch (e) {
    if (mine === seq) detailError.value = errorMessage(e, '지원자 정보를 불러오지 못했습니다.');
  } finally {
    if (mine === seq) detailLoading.value = false;
  }
}

function close() {
  selectedId.value = null;
  detail.value = null;
}

async function downloadPdf() {
  if (!detail.value) return;
  downloading.value = true;
  try {
    const { data } = await apiClient.get(`${base}/${detail.value.applicantId}/pdf-url`);
    // 서명 URL 이 「저장」 으로 내려오도록 만들어져 있어 화면은 그대로 남는다
    window.location.href = data.url;
  } catch (e) {
    alert(errorMessage(e, 'PDF 를 내려받지 못했습니다.'));
  } finally {
    setTimeout(() => (downloading.value = false), 800);
  }
}

function onKey(e) {
  if (!selectedId.value) return;
  if (e.key === 'Escape') close();
  else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    e.preventDefault();
    open(prevId.value);
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    e.preventDefault();
    open(nextId.value);
  }
}

onMounted(async () => {
  window.addEventListener('keydown', onKey);
  try {
    const { data } = await apiClient.get(base);
    rows.value = data.applicants ?? [];
    contestTitle.value = data.contestTitle ?? '';
  } catch (e) {
    loadError.value = errorMessage(e, '지원자 목록을 불러오지 못했습니다.');
  } finally {
    loading.value = false;
  }
});
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>

<style scoped>
.ap-head { display: flex; justify-content: space-between; align-items: flex-end; }
.ap-back { font-size: 13px; color: #555; text-decoration: none; }
.ap-title { margin: 6px 0 4px; font-size: 22px; }
.ap-sub, .ap-muted { color: #888; font-size: 13px; }
.ap-error { color: #c0392b; }
.ap-center { text-align: center; padding: 24px; }
.ap-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.ap-table th, .ap-table td { padding: 10px 12px; border-bottom: 1px solid #eee; text-align: left; }
.ap-table th { background: #f7f8fa; font-weight: 600; }
.ap-row { cursor: pointer; }
.ap-row:hover { background: #f5f8ff; }
.ap-badge { display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 3px; background: #eef1f6; color: #666; font-size: 11px; vertical-align: middle; }
.ap-badge--warn { background: #fdecea; color: #c0392b; }
.ap-btn { height: 32px; padding: 0 14px; border: 1px solid #d1d5db; border-radius: 4px; background: #fff; color: #333; font-size: 13px; cursor: pointer; }
.ap-btn:disabled { opacity: 0.45; cursor: default; }
.ap-btn--sm { height: 28px; padding: 0 10px; font-size: 12px; }
.ap-btn--primary { border-color: #2563eb; background: #2563eb; color: #fff; }

.ap-modal { position: fixed; inset: 0; z-index: 1000; background: rgba(15, 23, 42, 0.55); display: flex; align-items: center; justify-content: center; }
.ap-modal__box { width: min(1400px, 94vw); height: 90vh; background: #fff; border-radius: 8px; display: flex; overflow: hidden; }
.ap-side { width: 250px; border-right: 1px solid #eee; display: flex; flex-direction: column; background: #fafbfc; }
.ap-side__head { padding: 14px 16px; font-weight: 600; border-bottom: 1px solid #eee; }
.ap-side__list { list-style: none; margin: 0; padding: 0; overflow-y: auto; flex: 1; }
.ap-side__list li { display: flex; gap: 8px; align-items: center; padding: 9px 16px; cursor: pointer; font-size: 13px; border-bottom: 1px solid #f1f1f1; }
.ap-side__list li:hover { background: #eef3ff; }
.ap-side__list li.is-on { background: #2563eb; color: #fff; }
.ap-side__no { width: 28px; color: inherit; opacity: 0.7; }
.ap-side__name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ap-side__count { font-size: 11px; opacity: 0.7; }
.ap-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.ap-main__bar { display: flex; gap: 8px; align-items: center; padding: 10px 16px; border-bottom: 1px solid #eee; }
.ap-close { margin-left: auto; border: 0; background: none; font-size: 18px; cursor: pointer; color: #555; }
.ap-main__scroll { flex: 1; overflow-y: auto; padding: 20px 24px; }
.ap-main__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 20px; border-top: 1px solid #eee; background: #fafbfc; }

.ap-profile { display: flex; gap: 20px; align-items: center; margin-bottom: 16px; }
.ap-profile__img { width: 96px; height: 96px; flex: none; }
.ap-profile__img img { width: 96px; height: 96px; object-fit: cover; border-radius: 50%; }
.ap-profile__no { font-size: 12px; color: #888; }
.ap-profile h3 { margin: 4px 0; font-size: 20px; }
.ap-profile h3 small { font-size: 14px; color: #888; font-weight: 400; }
.ap-meta { display: flex; flex-wrap: wrap; gap: 12px; font-size: 13px; color: #555; margin: 4px 0 0; }
.ap-note { padding: 10px 12px; background: #fff7e6; border-radius: 4px; font-size: 13px; }
.ap-block { margin: 18px 0; }
.ap-block h4, .ap-block summary { font-size: 14px; font-weight: 700; margin: 0 0 8px; cursor: default; }
.ap-block summary { cursor: pointer; }
.ap-text { white-space: pre-wrap; font-size: 14px; line-height: 1.6; margin: 0; }
.ap-answers { display: grid; grid-template-columns: 200px 1fr; gap: 6px 16px; font-size: 13px; margin: 0; }
.ap-answers dt { color: #666; }
.ap-answers dd { margin: 0; white-space: pre-wrap; word-break: break-word; }
.ap-sections { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
.ap-section ul { margin: 0; padding-left: 16px; font-size: 13px; line-height: 1.7; }
.ap-works { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 18px; }
.ap-work { margin: 0; }
.ap-work__img { aspect-ratio: 1 / 1; background: #f3f4f6; border-radius: 4px; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.ap-work__img img { width: 100%; height: 100%; object-fit: contain; }
.ap-work figcaption { font-size: 13px; margin-top: 8px; }
.ap-work__meta { color: #666; font-size: 12px; margin-top: 2px; }
.ap-work__desc { font-size: 12px; color: #444; margin: 4px 0 0; white-space: pre-wrap; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
/* 지워진 이미지 — 회색 칸 */
.ap-deleted { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; background: repeating-linear-gradient(45deg, #e5e7eb, #e5e7eb 10px, #eceef1 10px, #eceef1 20px); color: #6b7280; text-align: center; padding: 8px; }
.ap-deleted span { font-size: 13px; font-weight: 600; }
.ap-deleted small { font-size: 11px; }
.ap-deleted--round { border-radius: 50%; }
</style>
